// Browser localStorage utilities for user progress tracking
// All user-specific data is namespaced by user email to ensure data isolation
import { auth, db } from '@/firebase/config';
import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';

const STORAGE_KEYS = {
  USER: 'medterm_user',
  PROGRESS: 'medterm_progress',
  MORPHEME_PROGRESS: 'medterm_morpheme_progress',
  FLASHCARD_PROGRESS: 'medterm_flashcard',
  QUIZ_SCORES: 'medterm_quiz_scores',
  MATCH_SCORES: 'medterm_match_scores',
  MORPHEME_SCORES: 'medterm_morpheme_scores',
  STUDY_STREAK: 'medterm_streak'
};

// Helper function to generate user-specific storage keys
const getUserStorageKey = (baseKey) => {
  const user = getUser();
  const email = user?.email || auth?.currentUser?.email;
  if (!email) {
    return `${baseKey}_guest`;
  }
  return `${baseKey}_${email}`;
};

// ── Flashcard Guest Daily Limit (Günde 5 Oyun Sınırı) ────────────────────────
export const MAX_GUEST_DAILY_FLASHCARDS = 5;

export const getFlashcardGuestDailyInfo = () => {
  const today = new Date().toISOString().split('T')[0];
  try {
    const raw = localStorage.getItem('medterm_flashcard_guest_daily');
    if (raw) {
      const data = JSON.parse(raw);
      if (data && data.date === today) {
        const count = typeof data.count === 'number' ? data.count : 0;
        return {
          date: today,
          count,
          remaining: Math.max(0, MAX_GUEST_DAILY_FLASHCARDS - count),
          canPlay: count < MAX_GUEST_DAILY_FLASHCARDS
        };
      }
    }
  } catch (e) {
    console.warn('[storage] getFlashcardGuestDailyInfo parse error:', e);
  }
  return {
    date: today,
    count: 0,
    remaining: MAX_GUEST_DAILY_FLASHCARDS,
    canPlay: true
  };
};

export const incrementFlashcardGuestPlay = () => {
  const today = new Date().toISOString().split('T')[0];
  const info = getFlashcardGuestDailyInfo();
  const nextCount = info.count + 1;
  try {
    localStorage.setItem('medterm_flashcard_guest_daily', JSON.stringify({
      date: today,
      count: nextCount
    }));
  } catch (e) {
    console.warn('[storage] incrementFlashcardGuestPlay write error:', e);
  }
  return {
    date: today,
    count: nextCount,
    remaining: Math.max(0, MAX_GUEST_DAILY_FLASHCARDS - nextCount),
    canPlay: nextCount < MAX_GUEST_DAILY_FLASHCARDS
  };
};

// ── Flashcard Guest Daily Restart Limit (Günde 10 Restart Sınırı) ─────────────
export const MAX_GUEST_DAILY_RESTARTS = 10;

export const getFlashcardGuestRestartInfo = () => {
  const today = new Date().toISOString().split('T')[0];
  try {
    const raw = localStorage.getItem('medterm_flashcard_guest_restart_daily');
    if (raw) {
      const data = JSON.parse(raw);
      if (data && data.date === today) {
        const count = typeof data.count === 'number' ? data.count : 0;
        return {
          date: today,
          count,
          remaining: Math.max(0, MAX_GUEST_DAILY_RESTARTS - count),
          canRestart: count < MAX_GUEST_DAILY_RESTARTS
        };
      }
    }
  } catch (e) {
    console.warn('[storage] getFlashcardGuestRestartInfo parse error:', e);
  }
  return {
    date: today,
    count: 0,
    remaining: MAX_GUEST_DAILY_RESTARTS,
    canRestart: true
  };
};

export const incrementFlashcardGuestRestart = () => {
  const today = new Date().toISOString().split('T')[0];
  const info = getFlashcardGuestRestartInfo();
  const nextCount = info.count + 1;
  try {
    localStorage.setItem('medterm_flashcard_guest_restart_daily', JSON.stringify({
      date: today,
      count: nextCount
    }));
  } catch (e) {
    console.warn('[storage] incrementFlashcardGuestRestart write error:', e);
  }
  return {
    date: today,
    count: nextCount,
    remaining: Math.max(0, MAX_GUEST_DAILY_RESTARTS - nextCount),
    canRestart: nextCount < MAX_GUEST_DAILY_RESTARTS
  };
};

// Guest 3-Day Trial Limit Utilities (Her iki paket için de 3 günlük misafir deneme süresi)
export const GUEST_TRIAL_DAYS = 3;
export const GUEST_TRIAL_MS = GUEST_TRIAL_DAYS * 24 * 60 * 60 * 1000;

export const getGuestTrialInfo = () => {
  let startTime = localStorage.getItem('medterm_guest_trial_start');
  if (!startTime) {
    startTime = Date.now().toString();
    localStorage.setItem('medterm_guest_trial_start', startTime);
  }

  const startMs = parseInt(startTime, 10) || Date.now();
  const elapsedMs = Math.max(0, Date.now() - startMs);
  const remainingMs = Math.max(0, GUEST_TRIAL_MS - elapsedMs);
  const isExpired = remainingMs <= 0;

  const daysLeft = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  const hoursLeft = Math.ceil(remainingMs / (60 * 60 * 1000));

  return {
    startMs,
    remainingMs,
    isExpired,
    daysLeft,
    hoursLeft
  };
};

export const canGuestPlay = () => {
  if (isLoggedIn()) return true;
  const trial = getGuestTrialInfo();
  return !trial.isExpired;
};

export const getGuestRemainingPlays = () => {
  if (isLoggedIn()) return Infinity;
  const trial = getGuestTrialInfo();
  return trial.daysLeft;
};

export const incrementGuestPlay = () => {
  return canGuestPlay();
};

// ── Türkçe Karakter ve İsim Biçimlendirici ──────────────────────────────────────
export const formatTurkishName = (name) => {
  if (!name || typeof name !== 'string') return '';
  const cleaned = name.replace(/[._-]+/g, ' ').trim();
  const asciiTurkishMap = {
    'cicek': 'Çiçek',
    'siddik': 'Sıddık',
    'ismail': 'İsmail',
    'ilker': 'İlker',
    'ilhan': 'İlhan',
    'ilknur': 'İlknur',
    'ilayda': 'İlayda',
    'cetin': 'Çetin',
    'caglar': 'Çağlar',
    'can': 'Can',
    'caner': 'Caner',
    'cem': 'Cem',
    'cemre': 'Cemre',
    'ugur': 'Uğur',
    'ozgur': 'Özgür',
    'gokce': 'Gökçe',
    'gokhan': 'Gökhan',
    'baris': 'Barış',
    'yagmur': 'Yağmur',
    'sukru': 'Şükrü',
    'senol': 'Şenol',
    'sevval': 'Şevval',
    'serife': 'Şerife',
    'ayse': 'Ayşe',
    'fatma': 'Fatma',
    'zeynep': 'Zeynep',
    'elif': 'Elif',
    'omer': 'Ömer',
    'osman': 'Osman',
    'huseyin': 'Hüseyin',
    'hasan': 'Hasan',
    'ibrahim': 'İbrahim',
    'mustafa': 'Mustafa',
    'emre': 'Emre',
    'burak': 'Burak',
    'ahmet': 'Ahmet',
    'mehmet': 'Mehmet',
    'alper': 'Alper',
    'alperen': 'Alperen'
  };

  return cleaned
    .split(/\s+/)
    .map(word => {
      if (!word) return '';
      const lower = word.toLowerCase();
      if (asciiTurkishMap[lower]) {
        return asciiTurkishMap[lower];
      }
      return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
    })
    .join(' ');
};

// ── Kullanıcı 3 Günlük Deneme Durumu (Dinamik & Gerçekçi) ───────────────────
export const getUserTrialState = (currentUser) => {
  const user = currentUser || getUser() || auth?.currentUser;
  if (!user) {
    return {
      hasTrial: false,
      isActive: false,
      isExpired: false,
      daysLeft: 0,
      currentDay: 0,
      endDate: null,
      nextBilledAt: null,
      remainingMs: 0,
      formattedEndDate: () => '',
      formattedBillingDate: () => '',
      amountText: () => '',
      summaryText: () => ''
    };
  }

  // Ömür Boyu üyeliklerde deneme/fatura asla olamaz
  const isLifetime =
    user.isLifetime === true ||
    user.planType === 'lifetime' ||
    (user.plan || '').toLowerCase().includes('lifetime') ||
    (user.plan || '').toLowerCase().includes('ömür');

  if (isLifetime) {
    return {
      hasTrial: false,
      isActive: false,
      isExpired: false,
      daysLeft: 0,
      currentDay: 0,
      endDate: null,
      nextBilledAt: null,
      remainingMs: 0,
      formattedEndDate: () => '',
      formattedBillingDate: () => '',
      amountText: () => '',
      summaryText: () => ''
    };
  }

  // 1. Kullanıcı GERÇEKTEN bir deneme planında mı?
  // - Paddle abonelik durumu 'trialing' ise
  // - Firestore veya local kullanıcıda isTrial / isTrialing bayrağı varsa
  // - Gerçek bir trialEndDate alanı tanımlanmışsa
  // - Geliştirici/test önizlemesi (previewRole === 'trial')
  const status = (user.subscriptionStatus || '').toLowerCase();
  const hasTrial =
    status !== 'active' &&
    (status === 'trialing' ||
      user.isTrial === true ||
      user.isTrialing === true ||
      !!user.trialEndDate ||
      user.previewRole === 'trial');

  if (!hasTrial) {
    return {
      hasTrial: false,
      isActive: false,
      isExpired: false,
      daysLeft: 0,
      currentDay: 0,
      endDate: null,
      nextBilledAt: null,
      remainingMs: 0,
      formattedEndDate: () => '',
      formattedBillingDate: () => '',
      amountText: () => '',
      summaryText: () => ''
    };
  }

  // 2. Gerçek bitiş tarihini (endDate) hesapla
  let endMs = null;
  if (user.trialEndDate) {
    if (typeof user.trialEndDate.toDate === 'function') {
      endMs = user.trialEndDate.toDate().getTime();
    } else if (typeof user.trialEndDate === 'number') {
      endMs = user.trialEndDate;
    } else if (user.trialEndDate.seconds) {
      endMs = user.trialEndDate.seconds * 1000;
    } else {
      const parsed = new Date(user.trialEndDate).getTime();
      if (!isNaN(parsed)) endMs = parsed;
    }
  }

  // trialEndDate henüz yazılmamışsa ama trialStartDate varsa (3 gün ekle)
  if (!endMs && user.trialStartDate) {
    const start = new Date(user.trialStartDate).getTime();
    if (!isNaN(start)) {
      endMs = start + (3 * 24 * 60 * 60 * 1000);
    }
  }

  // Local storage fallback (sadece gerçekten trial bayrağı olan kullanıcılar için)
  const storageKey = user.uid ? `healthlex_trial_start_${user.uid}` : 'healthlex_user_trial_start';
  if (!endMs) {
    const storedStart = localStorage.getItem(storageKey);
    if (storedStart) {
      const parsed = parseInt(storedStart, 10);
      if (!isNaN(parsed)) endMs = parsed + (3 * 24 * 60 * 60 * 1000);
    }
  }

  // Deneme yeni başladıysa ve endMs hala yoksa şu andan itibaren 3 gün başlat
  if (!endMs) {
    const now = Date.now();
    endMs = now + (3 * 24 * 60 * 60 * 1000);
    localStorage.setItem(storageKey, now.toString());
  }

  const now = Date.now();
  const remainingMs = Math.max(0, endMs - now);
  const isExpired = remainingMs <= 0;

  // Kalan gün sayısı: Süre dolmuşsa kesin 0, dolmamışsa yukarı yuvarla (1, 2 veya 3)
  const daysLeft = isExpired ? 0 : Math.min(3, Math.max(1, Math.ceil(remainingMs / (24 * 60 * 60 * 1000))));
  const currentDay = isExpired ? 4 : Math.min(3, Math.max(1, 4 - daysLeft));
  const endDate = new Date(endMs);

  let nextBilledAt = endDate;
  if (user.nextBilledAt) {
    if (typeof user.nextBilledAt.toDate === 'function') {
      nextBilledAt = user.nextBilledAt.toDate();
    } else {
      const parsed = new Date(user.nextBilledAt);
      if (!isNaN(parsed.getTime())) nextBilledAt = parsed;
    }
  }

  const isBasic =
    user.isBasic === true ||
    user.planType === 'basic' ||
    (user.plan || '').toLowerCase().includes('basic') ||
    (user.plan || '').toLowerCase().includes('temel');

  const formattedEndDate = (lang = 'tr') => {
    return endDate.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formattedBillingDate = (lang = 'tr') => {
    return nextBilledAt.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const amountText = (isTr = true) => {
    if (isBasic) {
      return isTr ? '₺790/yıl' : '₺790/yr';
    }
    return isTr ? '₺2.000/yıl' : '₺2,000/yr';
  };

  const summaryText = (lang = 'tr') => {
    const isTr = lang === 'tr';
    if (isTr) {
      return `Deneme bitişi: ${formattedEndDate('tr')} · İlk tahsilat: ${formattedBillingDate('tr')} · Tutar: ${amountText(true)}`;
    }
    return `Trial ends: ${formattedEndDate('en')} · First billing: ${formattedBillingDate('en')} · Amount: ${amountText(false)}`;
  };

  return {
    hasTrial: true,
    isActive: !isExpired,
    isExpired,
    daysLeft,
    currentDay,
    endDate,
    nextBilledAt,
    remainingMs,
    formattedEndDate,
    formattedBillingDate,
    amountText,
    summaryText
  };
};

// User management
export const saveUser = (userData) => {
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
};

export const getUser = () => {
  const data = localStorage.getItem(STORAGE_KEYS.USER);
  if (data) {
    try {
      const parsed = JSON.parse(data);
      if (parsed && (parsed.uid || parsed.email)) {
        return parsed;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
  }
  if (auth && auth.currentUser) {
    const fallbackUser = {
      uid: auth.currentUser.uid,
      name: auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'User',
      email: auth.currentUser.email,
      joinDate: auth.currentUser.metadata?.creationTime || new Date().toISOString()
    };
    saveUser(fallbackUser);
    return fallbackUser;
  }
  return null;
};

export const isLoggedIn = () => {
  // LocalStorage'da geçerli kullanıcı verisi varsa → giriş yapmış
  if (getUser() !== null) return true;
  // Firebase Auth'da gerçek (anonim olmayan) oturum varsa → giriş yapmış
  if (auth && auth.currentUser && !auth.currentUser.isAnonymous) return true;
  return false;
};

export const logout = () => {
  const user = getUser();
  const uid = user?.uid || auth?.currentUser?.uid;

  // Clear local progress cache for the logged out user
  const progressKey = getUserStorageKey(STORAGE_KEYS.PROGRESS);
  if (progressKey) {
    localStorage.removeItem(progressKey);
  }
  
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem('healthlex_user_trial_start');
  if (uid) {
    localStorage.removeItem(`healthlex_trial_start_${uid}`);
  }
};

// Permanently delete all user progress and scores from Firestore and LocalStorage
export const clearAllUserData = async (currentUser) => {
  const user = currentUser || getUser();
  const uid = user?.uid;
  const email = user?.email;

  // 1. Delete all user_progress documents in Firestore for both UID and Email identifiers
  const identifiers = Array.from(new Set([uid, email].filter(Boolean)));
  for (const identifier of identifiers) {
    try {
      const progressRef = collection(db, 'user_progress');
      const q = query(progressRef, where('userId', '==', identifier));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        let batch = writeBatch(db);
        let uncommittedOps = 0;
        for (const docSnap of snapshot.docs) {
          batch.delete(docSnap.ref);
          uncommittedOps++;
          if (uncommittedOps === 400) {
            await batch.commit();
            batch = writeBatch(db);
            uncommittedOps = 0;
          }
        }
        if (uncommittedOps > 0) {
          await batch.commit();
        }
        console.log(`[Firestore] Deleted ${snapshot.size} user_progress documents for identifier: ${identifier}`);
      }
    } catch (err) {
      console.error('[Firestore] Error deleting user progress for:', identifier, err);
    }
  }

  // 2. Clear all user namespaced keys from localStorage
  if (email) {
    Object.values(STORAGE_KEYS).forEach((baseKey) => {
      localStorage.removeItem(`${baseKey}_${email}`);
    });
  }
  if (uid) {
    Object.values(STORAGE_KEYS).forEach((baseKey) => {
      localStorage.removeItem(`${baseKey}_${uid}`);
    });
  }
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem('user_preferences');

  // Also remove guest keys if any
  Object.values(STORAGE_KEYS).forEach((baseKey) => {
    localStorage.removeItem(`${baseKey}_guest`);
  });
};

// Progress tracking
export const saveProgress = async (termId, isLearned) => {
  try {
    const key = getUserStorageKey(STORAGE_KEYS.PROGRESS);
    if (!key) {
      console.error('[storage] saveProgress: Failed to determine user storage key for termId:', termId);
      return;
    }

    // 1. Update local storage cache immediately for fast UI feedback
    const progress = getProgress();
    progress[termId] = {
      learned: isLearned,
      lastReviewed: new Date().toISOString(),
      reviewCount: (progress[termId]?.reviewCount || 0) + 1
    };
    localStorage.setItem(key, JSON.stringify(progress));

    // 2. Synchronize to Firestore in the background (only if user is actually authenticated with Firebase Auth)
    const currentUid = auth?.currentUser?.uid;
    if (currentUid) {
      try {
        const docId = `${currentUid}_${termId}`;
        const progressDocRef = doc(db, 'user_progress', docId);

        if (isLearned) {
          await setDoc(progressDocRef, {
            userId: currentUid,
            termId: termId,
            status: 'learned',
            lastReviewed: new Date().toISOString(),
            reviewCount: progress[termId].reviewCount
          });
        } else {
          await deleteDoc(progressDocRef);
        }
      } catch (error) {
        console.error('[storage] Error syncing progress to Firestore for termId:', termId, error);
      }
    }
  } catch (err) {
    console.error('[storage] Critical error in saveProgress for termId:', termId, err);
  }
};

// Synchronize progress from Firestore back into localStorage
export const syncProgressFromFirestore = async () => {
  const user = getUser();
  const userIdentifier = auth?.currentUser?.uid || user?.uid;
  if (!userIdentifier) return;

  try {
    const progressRef = collection(db, 'user_progress');
    const q = query(progressRef, where('userId', '==', userIdentifier));
    const querySnapshot = await getDocs(q);

    const progress = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'learned') {
        progress[data.termId] = {
          learned: true,
          lastReviewed: data.lastReviewed || new Date().toISOString(),
          reviewCount: data.reviewCount || 1
        };
      }
    });

    const key = getUserStorageKey(STORAGE_KEYS.PROGRESS);
    if (key) {
      localStorage.setItem(key, JSON.stringify(progress));
    }
    console.log('User progress successfully synchronized from Firestore.');
  } catch (error) {
    console.error('Error syncing progress from Firestore:', error);
  }
};

export const getProgress = () => {
  const key = getUserStorageKey(STORAGE_KEYS.PROGRESS);
  if (!key) return {}; // No user logged in

  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : {};
};

export const getTermProgress = (termId) => {
  const progress = getProgress();
  return progress[termId] || { learned: false, reviewCount: 0 };
};

export const getLearnedCount = () => {
  const progress = getProgress();
  return Object.values(progress).filter(p => p.learned).length;
};

// Flashcard progress
export const saveFlashcardSession = (categoryId, completedCount, totalCount) => {
  const key = getUserStorageKey(STORAGE_KEYS.FLASHCARD_PROGRESS);
  if (!key) return; // No user logged in

  const sessions = getFlashcardSessions();
  sessions.push({
    categoryId,
    completedCount,
    totalCount,
    date: new Date().toISOString()
  });
  localStorage.setItem(key, JSON.stringify(sessions));
};

export const getFlashcardSessions = () => {
  const key = getUserStorageKey(STORAGE_KEYS.FLASHCARD_PROGRESS);
  if (!key) return []; // No user logged in

  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

// Quiz scores
export const saveQuizScore = (categoryId, score, total) => {
  const key = getUserStorageKey(STORAGE_KEYS.QUIZ_SCORES);
  if (!key) return; // No user logged in

  const scores = getQuizScores();
  scores.push({
    categoryId,
    score,
    total,
    percentage: Math.round((score / total) * 100),
    date: new Date().toISOString()
  });
  localStorage.setItem(key, JSON.stringify(scores));
};

export const getQuizScores = () => {
  const key = getUserStorageKey(STORAGE_KEYS.QUIZ_SCORES);
  if (!key) return []; // No user logged in

  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

export const getAverageQuizScore = () => {
  const scores = getQuizScores();
  if (scores.length === 0) return 0;
  const sum = scores.reduce((acc, s) => acc + s.percentage, 0);
  return Math.round(sum / scores.length);
};

// Match game scores
export const saveMatchScore = (categoryId, time, moves) => {
  const key = getUserStorageKey(STORAGE_KEYS.MATCH_SCORES);
  if (!key) return; // No user logged in

  const scores = getMatchScores();
  scores.push({
    categoryId,
    time,
    moves,
    date: new Date().toISOString()
  });
  localStorage.setItem(key, JSON.stringify(scores));
};

export const getMatchScores = () => {
  const key = getUserStorageKey(STORAGE_KEYS.MATCH_SCORES);
  if (!key) return []; // No user logged in

  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

// Morpheme game scores
export const saveMorphemeScore = (score, total, percentage) => {
  const key = getUserStorageKey(STORAGE_KEYS.MORPHEME_SCORES);
  if (!key) return;

  const scores = getMorphemeScores();
  scores.push({
    score,
    total,
    percentage,
    date: new Date().toISOString()
  });
  localStorage.setItem(key, JSON.stringify(scores));
};

export const getMorphemeScores = () => {
  const key = getUserStorageKey(STORAGE_KEYS.MORPHEME_SCORES);
  if (!key) return [];

  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

// ── Morpheme Progress Tracking ──────────────────────────────────────────────
export const saveMorphemeProgress = async (morphemeId, isLearned) => {
  try {
    const key = getUserStorageKey(STORAGE_KEYS.MORPHEME_PROGRESS);
    if (!key) return;

    // 1. Update local storage cache immediately for fast UI feedback
    const progress = getMorphemeProgress();
    progress[morphemeId] = {
      learned: isLearned,
      lastReviewed: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(progress));

    // 2. Synchronize to Firestore in the background
    const currentUid = auth?.currentUser?.uid;
    if (currentUid) {
      try {
        const docId = `${currentUid}_${morphemeId}`;
        const morphemeDocRef = doc(db, 'user_morpheme_progress', docId);

        if (isLearned) {
          await setDoc(morphemeDocRef, {
            userId: currentUid,
            morphemeId: morphemeId,
            status: 'learned',
            lastReviewed: new Date().toISOString()
          });
        } else {
          await deleteDoc(morphemeDocRef);
        }
      } catch (error) {
        console.error('[storage] Error syncing morpheme progress to Firestore:', morphemeId, error);
      }
    }
  } catch (err) {
    console.error('[storage] Critical error in saveMorphemeProgress:', morphemeId, err);
  }
};

export const getMorphemeProgress = () => {
  const key = getUserStorageKey(STORAGE_KEYS.MORPHEME_PROGRESS);
  if (!key) return {};
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : {};
};

export const getMorphemeStatus = (morphemeId) => {
  const progress = getMorphemeProgress();
  return progress[morphemeId] || { learned: false };
};

export const getLearnedMorphemesCount = () => {
  const progress = getMorphemeProgress();
  return Object.values(progress).filter((p) => p.learned).length;
};

export const syncMorphemeProgressFromFirestore = async () => {
  const user = getUser();
  const userIdentifier = auth?.currentUser?.uid || user?.uid;
  if (!userIdentifier) return;

  try {
    const progressRef = collection(db, 'user_morpheme_progress');
    const q = query(progressRef, where('userId', '==', userIdentifier));
    const querySnapshot = await getDocs(q);

    const progress = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'learned') {
        progress[data.morphemeId] = {
          learned: true,
          lastReviewed: data.lastReviewed || new Date().toISOString()
        };
      }
    });

    const key = getUserStorageKey(STORAGE_KEYS.MORPHEME_PROGRESS);
    if (key) {
      localStorage.setItem(key, JSON.stringify(progress));
    }
    console.log('[storage] Morpheme progress synchronized from Firestore.');
  } catch (error) {
    console.error('[storage] Error syncing morpheme progress from Firestore:', error);
  }
};

// ── Giriş / Ziyaret Bazlı Günlük Seri (Calendar-based Study Streak) ────────────

/**
 * Normalizes any Date instance, ISO string, or local YYYY-MM-DD string
 * into a pure local calendar date string "YYYY-MM-DD" using user's local timezone.
 */
export const normalizeLocalDateStr = (val) => {
  if (!val) return null;
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return val;
  }
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns exact difference in calendar days between two dates.
 * Both dates are converted to UTC midnight from their local year/month/day components.
 */
export const getDiffCalendarDays = (prevDateVal, nextDateVal) => {
  const prevStr = normalizeLocalDateStr(prevDateVal);
  const nextStr = normalizeLocalDateStr(nextDateVal);
  if (!prevStr || !nextStr) return null;

  const [py, pm, pd] = prevStr.split('-').map(Number);
  const [ny, nm, nd] = nextStr.split('-').map(Number);
  const prevUtc = Date.UTC(py, pm - 1, pd);
  const nextUtc = Date.UTC(ny, nm - 1, nd);
  return Math.round((nextUtc - prevUtc) / (1000 * 60 * 60 * 24));
};

/**
 * Merges local and remote streak records.
 * Rules:
 * 1. Takes the record whose lastStudyDate is newer AS A WHOLE (currentStreak, totalDays, lastStudyDate).
 * 2. Exception: longestStreak always takes the maximum of both records (records never drop).
 */
export const mergeStreakRecords = (localStreak = {}, remoteStreak = {}) => {
  const localDateStr = normalizeLocalDateStr(localStreak.lastStudyDate);
  const remoteDateStr = normalizeLocalDateStr(remoteStreak.lastStudyDate);

  let chosen;
  if (localDateStr && remoteDateStr) {
    if (localDateStr > remoteDateStr) {
      chosen = { ...localStreak, lastStudyDate: localDateStr };
    } else if (remoteDateStr > localDateStr) {
      chosen = { ...remoteStreak, lastStudyDate: remoteDateStr };
    } else {
      // Same calendar day: take the one with greater or equal currentStreak
      chosen = (localStreak.currentStreak || 0) >= (remoteStreak.currentStreak || 0)
        ? { ...localStreak, lastStudyDate: localDateStr }
        : { ...remoteStreak, lastStudyDate: remoteDateStr };
    }
  } else if (localDateStr) {
    chosen = { ...localStreak, lastStudyDate: localDateStr };
  } else if (remoteDateStr) {
    chosen = { ...remoteStreak, lastStudyDate: remoteDateStr };
  } else {
    chosen = { ...localStreak };
  }

  return {
    currentStreak: chosen.currentStreak || 0,
    longestStreak: Math.max(
      localStreak.longestStreak || 0,
      remoteStreak.longestStreak || 0,
      chosen.currentStreak || 0
    ),
    totalDays: Math.max(localStreak.totalDays || 0, remoteStreak.totalDays || 0, chosen.totalDays || 0),
    lastStudyDate: chosen.lastStudyDate ? normalizeLocalDateStr(chosen.lastStudyDate) : null
  };
};

/**
 * Calculates next streak state given current state and target date.
 */
export const calculateNextStreak = (currentStreakState = {}, targetDate = new Date()) => {
  const streak = {
    currentStreak: Number(currentStreakState.currentStreak) || 0,
    longestStreak: Number(currentStreakState.longestStreak) || 0,
    totalDays: Number(currentStreakState.totalDays) || 0,
    lastStudyDate: currentStreakState.lastStudyDate || null
  };

  const todayStr = normalizeLocalDateStr(targetDate);
  const lastDateStr = normalizeLocalDateStr(streak.lastStudyDate);

  if (lastDateStr === todayStr) {
    // Already counted today
    return { streak, updated: false };
  }

  if (!lastDateStr) {
    // First active day
    streak.currentStreak = 1;
    streak.longestStreak = Math.max(streak.longestStreak || 0, 1);
    streak.totalDays = (streak.totalDays || 0) + 1;
  } else {
    const diffDays = getDiffCalendarDays(lastDateStr, todayStr);

    if (diffDays === 1) {
      // Consecutive calendar day! Streak increases
      streak.currentStreak = (streak.currentStreak || 0) + 1;
      streak.longestStreak = Math.max(streak.longestStreak || 0, streak.currentStreak);
      streak.totalDays = (streak.totalDays || 0) + 1;
    } else if (diffDays > 1) {
      // Streak broken, reset to 1
      streak.currentStreak = 1;
      streak.totalDays = (streak.totalDays || 0) + 1;
    } else {
      // Clock skew or earlier date, keep existing
      return { streak, updated: false };
    }
  }

  streak.lastStudyDate = todayStr;
  return { streak, updated: true };
};

export const updateStreak = async (customDate = new Date()) => {
  const key = getUserStorageKey(STORAGE_KEYS.STUDY_STREAK);
  const currentStreak = getStreak();
  const { streak, updated } = calculateNextStreak(currentStreak, customDate);

  if (!updated) {
    return streak;
  }

  if (key) {
    localStorage.setItem(key, JSON.stringify(streak));
  }

  // Synchronize to Firestore users/{uid}
  const currentUid = auth?.currentUser?.uid;
  if (currentUid) {
    try {
      const userRef = doc(db, 'users', currentUid);
      await setDoc(userRef, {
        streak: {
          currentStreak: streak.currentStreak,
          longestStreak: streak.longestStreak,
          lastStudyDate: streak.lastStudyDate,
          totalDays: streak.totalDays
        }
      }, { merge: true });
    } catch (err) {
      console.warn('[storage] Error syncing streak to Firestore:', err);
    }
  }

  return streak;
};

export const syncStreakFromFirestore = async () => {
  const currentUid = auth?.currentUser?.uid || getUser()?.uid;
  if (!currentUid) return;

  try {
    const userRef = doc(db, 'users', currentUid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data.streak) {
        const localStreak = getStreak();
        const mergedStreak = mergeStreakRecords(localStreak, data.streak);
        const key = getUserStorageKey(STORAGE_KEYS.STUDY_STREAK);
        if (key) {
          localStorage.setItem(key, JSON.stringify(mergedStreak));
        }
        return mergedStreak;
      }
    }
  } catch (err) {
    console.warn('[storage] Error syncing streak from Firestore:', err);
  }
};

export const getStreak = () => {
  const key = getUserStorageKey(STORAGE_KEYS.STUDY_STREAK);
  if (!key) return { currentStreak: 0, longestStreak: 0, totalDays: 0, lastStudyDate: null };

  const data = localStorage.getItem(key);
  if (!data) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalDays: 0,
      lastStudyDate: null
    };
  }

  try {
    const parsed = JSON.parse(data);
    return {
      currentStreak: Number(parsed.currentStreak) || 0,
      longestStreak: Number(parsed.longestStreak) || 0,
      totalDays: Number(parsed.totalDays) || 0,
      lastStudyDate: parsed.lastStudyDate ? normalizeLocalDateStr(parsed.lastStudyDate) : null
    };
  } catch (e) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalDays: 0,
      lastStudyDate: null
    };
  }
};

// ── Seviye Hesaplama (Level Logic) ──────────────────────────────────────────
export const getUserLevel = (learnedTerms = 0, learnedMorphemes = 0) => {
  const total = (Number(learnedTerms) || 0) + (Number(learnedMorphemes) || 0);
  if (total >= 400) return { level: 6, titleTr: 'Başhekim', titleEn: 'Chief Physician', nextThreshold: null, currentTotal: total };
  if (total >= 250) return { level: 5, titleTr: 'Uzman', titleEn: 'Specialist', nextThreshold: 400, currentTotal: total };
  if (total >= 120) return { level: 4, titleTr: 'Asistan', titleEn: 'Resident', nextThreshold: 250, currentTotal: total };
  if (total >= 50)  return { level: 3, titleTr: 'Pratisyen', titleEn: 'Practitioner', nextThreshold: 120, currentTotal: total };
  if (total >= 15)  return { level: 2, titleTr: 'Çırak', titleEn: 'Apprentice', nextThreshold: 50, currentTotal: total };
  return { level: 1, titleTr: 'Başlangıç', titleEn: 'Beginner', nextThreshold: 15, currentTotal: total };
};

// Stats summary
export const getStats = () => {
  const progress = getProgress();
  const learnedCount = Object.values(progress).filter(p => p.learned).length;
  const learnedMorphemes = getLearnedMorphemesCount();
  const totalReviews = Object.values(progress).reduce((acc, p) => acc + p.reviewCount, 0);
  const streak = getStreak();
  const quizAvg = getAverageQuizScore();
  const quizCount = getQuizScores().length;
  const matchCount = getMatchScores().length;
  const userLevel = getUserLevel(learnedCount, learnedMorphemes);

  return {
    learnedTerms: learnedCount,
    learnedMorphemes,
    totalReviews,
    currentStreak: streak.currentStreak,
    longestStreak: streak.longestStreak,
    totalDays: streak.totalDays,
    averageQuizScore: quizAvg,
    quizzesTaken: quizCount,
    matchGamesPlayed: matchCount,
    userLevel
  };
};